import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef
} from 'react';
import _uniqueId from 'lodash/uniqueId';
import KEY_CODES from 'common/KeyCodes';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

import { useClickOutside, useKeyDownOutside } from '../common';
import useAnimation from '../common/useAnimation';


const RIPPLE_STYLE = {
  position: 'absolute' as const,
  borderRadius: '100%',
  zIndex: 0,
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none' as const,
};

const ANIMATION_DURATION = parseInt(Tokens.SPEED.SPEED_SLOW, 10);
const TIMEOUT_DURATION = ANIMATION_DURATION + 20;

export interface RippleEffectProps extends React.ComponentPropsWithoutRef<'div'> {
  style?: React.CSSProperties;
  rippleStyle?: React.CSSProperties;
  children: React.ReactNode;
  disabled?: boolean;
  center?: boolean;
  as?: string;
  onMouseDown?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
  onBlur?: React.FocusEventHandler;
}

export type RippleEffectHandle = {
  handleKeyDown: (any) => void,
  handleMouseDown: (any) => void,
};

const RippleEffect = forwardRef<RippleEffectHandle, RippleEffectProps>((props: RippleEffectProps, ref) => {
  const {
    style,
    disabled,
    children,
    center,
    as,
    rippleStyle,
    onMouseDown,
    onMouseLeave,
    onMouseUp,
    onKeyDown,
    onBlur,
    ...otherProps
  } = props;
  const clickedOutSide = useRef(false);
  const fadeTimeout = useRef(null);
  const unmount = useRef(false);
  const wrapperRef = useRef(null);
  const mouseUp = useRef(false);
  const timeoutReached = useRef(false);
  const triggeredByKeyDown = useRef(false);
  const fading = useRef(false);
  const running = useRef(false);
  const targetRadius = useRef(0);
  const top = useRef(0);
  const left = useRef(0);
  const currentOpacity = useRef(0);

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const [fadeTimeoutTrigger, setFadeTimeoutTrigger] = useState('0');

  const [run, setRun] = useState('initial-id');
  const animatedValue = useAnimation(Tokens.TRANSITION.TRANSITION_DECELERATE, ANIMATION_DURATION, run);

  const fade = () => {
    if (unmount.current) {
      return;
    }
    running.current = false;
    fading.current = true;
    currentOpacity.current = opacity;
    setRun(_uniqueId());
  };

  const handleMouseUp = (event) => {
    if (event.button !== 0 || disabled) {
      return;
    }
    mouseUp.current = true;
    if (timeoutReached.current) {
      fade();
    }
  };

  const handleMouseLeave = () => {
    if (disabled || mouseUp.current || !running.current) {
      return;
    }
    fade();
  };

  const cleanUpFade = () => {
    fadeTimeout.current && clearTimeout(fadeTimeout.current);
  };

  const handleMouseDown = (event) => {
    clickedOutSide.current = false;
    if (event.button !== 0 || disabled) {
      return;
    }
    fading.current = false;
    running.current = true;
    timeoutReached.current = false;
    mouseUp.current = false;
    triggeredByKeyDown.current = false;
    setWidth(0);
    setHeight(0);
    setOpacity(0);
    cleanUpFade();
    const { pageX, pageY, currentTarget } = event;
    const rect = currentTarget.getBoundingClientRect();
    left.current = center ? rect.width / 2 : pageX - ((rect.left) as number + window.scrollX);
    top.current = center ? rect.height / 2 : pageY - ((rect.top) as number + window.scrollY);
    const radius = (left.current > rect.width / 2 ? left.current : ((rect.width / 2) - left.current) + (rect.width / 2));
    targetRadius.current = (radius * 2.1);
    setFadeTimeoutTrigger(_uniqueId());
    setRun(_uniqueId());
  };

  useEffect(() => {
    fadeTimeout.current = setTimeout(() => {
      timeoutReached.current = true;
      if (mouseUp.current || triggeredByKeyDown.current) {
        fade();
      }
    }, TIMEOUT_DURATION);
  }, [fadeTimeoutTrigger]);

  useEffect(() => {
    if (clickedOutSide.current) {
      return;
    }
    !fading.current && setWidth(targetRadius.current * animatedValue);
    !fading.current && setHeight(targetRadius.current * animatedValue);
    // fade out
    if (fading.current) {
      // fade after mouse out
      if (!timeoutReached.current) {
        setOpacity(currentOpacity.current * (1 - animatedValue));
      } else { // fade after complete click
        setOpacity(1 - animatedValue);
      }
    } else { // fade in
      setOpacity(animatedValue);
    }
  }, [animatedValue]);

  useEffect(() => () => { unmount.current = true; }, []);

  const handleKeyDown = (event) => {
    clickedOutSide.current = false;
    if (disabled) {
      return;
    }
    fading.current = false;
    running.current = true;
    timeoutReached.current = false;
    triggeredByKeyDown.current = true;
    setWidth(0);
    setHeight(0);
    setOpacity(0);
    cleanUpFade();
    const { currentTarget } = event;
    const rect = currentTarget.getBoundingClientRect();
    left.current = rect.width / 2;
    top.current = rect.height / 2;
    const radius = (left.current > rect.width / 2 ? left.current : ((rect.width / 2) - left.current) + (rect.width / 2));
    targetRadius.current = (radius * 2.1);
    setFadeTimeoutTrigger(_uniqueId());
    setRun(_uniqueId());
  };

  useImperativeHandle(ref, () => ({
    handleKeyDown,
    handleMouseDown,
  }));

  const handleClickOutSide = () => {
    clickedOutSide.current = true;
    setWidth(0);
    setHeight(0);
    setOpacity(0);
    cleanUpFade();
  };

  useClickOutside(wrapperRef, handleClickOutSide);
  useKeyDownOutside(wrapperRef, handleClickOutSide);

  const wrapperProps = {
    style: {
      maxWidth: '100%',
      backgroundColor: 'transparent',
      position: 'relative' as const,
      display: 'inline-flex',
      overflow: 'hidden',
      height: 'fit-content',
      ...style,
    },
    onMouseLeave: (event) => {
      handleMouseLeave();
      onMouseLeave && onMouseLeave(event);
    },
    onMouseDown: (event) => {
      handleMouseDown(event);
      onMouseDown && onMouseDown(event);
    },
    onMouseUp: (event) => {
      handleMouseUp(event);
      onMouseUp && onMouseUp(event);
    },
    onKeyDown: (event) => {
      (event.key === KEY_CODES.ENTER_KEY || event.key === KEY_CODES.SPACE_KEY)
        && handleKeyDown(event);
      onKeyDown && onKeyDown(event);
    },
    ref: wrapperRef,
    onBlur: (event) => {
      fade();
      onBlur && onBlur(event);
    },
    ...otherProps
  };
  const Component = as;
  return (
    <Component {...wrapperProps}>
      {children}
      <div
        style={{
          cursor: disabled ? 'default' : 'pointer',
          ...RIPPLE_STYLE,
          ...rippleStyle,
          left: left.current,
          top: top.current,
          opacity,
          width,
          height
        }}
      />
    </Component>
  );
});


RippleEffect.defaultProps = {
  rippleStyle: {},
  style: {},
  disabled: false,
  center: false,
  as: 'div',
  onMouseDown: undefined,
  onMouseLeave: undefined,
  onMouseUp: undefined,
  onKeyDown: undefined,
  onBlur: undefined,
};
RippleEffect.displayName = 'RippleEffect';
export default RippleEffect;
